// src/services/statisticsService.js

const API_URL = `${process.env.REACT_APP_BASE_URL}/api/v2/statistics`;

// Builds the connection-metric URL with every supported optional filter.
// Backwards compatible: callers that only pass page still work.
const buildConnectionUrl = (endpoint, opts = {}) => {
  const params = new URLSearchParams();
  if (opts.page != null) params.append("page", opts.page);
  if (opts.isAsc !== null && opts.isAsc !== undefined) params.append("isAsc", opts.isAsc);
  if (opts.sortBy) params.append("sortBy", opts.sortBy);
  if (opts.startDate) params.append("startDate", opts.startDate);
  if (opts.endDate) params.append("endDate", opts.endDate);
  if (opts.staId != null) params.append("staId", opts.staId);
  if (opts.citId != null) params.append("citId", opts.citId);
  if (opts.surId != null) params.append("surId", opts.surId);
  if (opts.rouId != null) params.append("rouId", opts.rouId);

  const qs = params.toString();
  return `${API_URL}/bus-metrics/${endpoint}${qs ? `?${qs}` : ""}`;
};

const fetchConnection = async (endpoint, token, opts = {}) => {
  try {
    const url = buildConnectionUrl(endpoint, opts);
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${endpoint}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`❌ Error en ${endpoint}:`, error);
    return null;
  }
};

// ==========================================
// PAGINATED CONNECTION METRICS
// ==========================================
//
// Each function accepts (token, opts) where opts can include:
//   page, isAsc, sortBy, startDate, endDate, staId, citId, surId, rouId
//
// Legacy positional calls — getAllBusesConnectionMetrics(token, 0) — still work
// because page can be passed either positionally as second arg or inside opts.
const normalizeOpts = (pageOrOpts, maybeIsAsc, maybeSortBy) => {
  if (pageOrOpts !== null && typeof pageOrOpts === "object") {
    return pageOrOpts;
  }
  const opts = {};
  if (pageOrOpts != null) opts.page = pageOrOpts;
  if (maybeIsAsc !== undefined && maybeIsAsc !== null) opts.isAsc = maybeIsAsc;
  if (maybeSortBy) opts.sortBy = maybeSortBy;
  return opts;
};

export const getAllBusesConnectionMetrics = (token, pageOrOpts = 0, isAsc, sortBy) =>
  fetchConnection("all-connections", token, normalizeOpts(pageOrOpts, isAsc, sortBy));

export const getConnectedBusesMetrics = (token, pageOrOpts = 0, isAsc, sortBy) =>
  fetchConnection("connected", token, normalizeOpts(pageOrOpts, isAsc, sortBy));

export const getDisconnectedBusesMetrics = (token, pageOrOpts = 0, isAsc, sortBy) =>
  fetchConnection("disconnected", token, normalizeOpts(pageOrOpts, isAsc, sortBy));

// ==========================================
// UNPAGINATED CONNECTION METRICS (LIST)
// Used to populate cascade filter dropdowns and to feed Excel exports
// without iterating through every paginated page.
// ==========================================

export const getAllBusesConnectionMetricsList = (token, opts = {}) =>
  fetchConnection("all-connections-list", token, opts);

export const getConnectedBusesList = (token, opts = {}) =>
  fetchConnection("connected-list", token, opts);

export const getDisconnectedBusesList = (token, opts = {}) =>
  fetchConnection("disconnected-list", token, opts);

// ==========================================
// LEGACY: bus activity (scan-based) endpoints from the CMS pattern.
// Kept for compatibility in case other components adopted them; not used by
// the BusActivityTab anymore.
// ==========================================

const fetchActivityMetrics = async (endpoint, token, params = {}, dateRange = null) => {
  try {
    const search = new URLSearchParams();
    search.append("page", params.page ?? 0);
    if (params.isAsc !== null && params.isAsc !== undefined) search.append("isAsc", params.isAsc);
    if (params.sortBy) search.append("sortBy", params.sortBy);
    if (params.staId != null) search.append("staId", params.staId);
    if (params.citId != null) search.append("citId", params.citId);
    if (params.surId != null) search.append("surId", params.surId);
    if (params.rouId != null) search.append("rouId", params.rouId);

    const path = dateRange?.startDate && dateRange?.endDate
      ? `${endpoint}/${dateRange.startDate}/${dateRange.endDate}`
      : endpoint;

    const url = `${API_URL}/bus-metrics/${path}?${search.toString()}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) throw new Error(`Error ${response.status}: ${endpoint}`);
    return await response.json();
  } catch (error) {
    console.error(`❌ Error en ${endpoint}:`, error);
    return null;
  }
};

const fetchActivityList = async (endpoint, token, params = {}) => {
  try {
    const search = new URLSearchParams();
    if (params.startDate) search.append("startDate", params.startDate);
    if (params.endDate) search.append("endDate", params.endDate);
    if (params.staId != null) search.append("staId", params.staId);
    if (params.citId != null) search.append("citId", params.citId);
    if (params.surId != null) search.append("surId", params.surId);
    if (params.rouId != null) search.append("rouId", params.rouId);
    const qs = search.toString();
    const url = `${API_URL}/bus-metrics/${endpoint}${qs ? `?${qs}` : ""}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) throw new Error(`Error ${response.status}: ${endpoint}`);
    return await response.json();
  } catch (error) {
    console.error(`❌ Error en ${endpoint}:`, error);
    return null;
  }
};

export const getActiveBuses = (token, params, dateRange) => fetchActivityMetrics("active-buses", token, params, dateRange);
export const getMostActiveBuses = (token, params, dateRange) => fetchActivityMetrics("most-active-buses", token, params, dateRange);
export const getInactiveBuses = (token, params, dateRange) => fetchActivityMetrics("inactive-buses", token, params, dateRange);

export const getMostActiveBusesList = (token, params) => fetchActivityList("most-active-buses-list", token, params);
export const getActiveBusesList = (token, params) => fetchActivityList("active-buses-list", token, params);
export const getInactiveBusesList = (token, params) => fetchActivityList("inactive-buses-list", token, params);
